// eslint-disable-next-line no-restricted-imports
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import { parseISO } from "date-fns";
import { type FC, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";

import { formatAssessmentCategory, getAssessmentStatusLabel } from "../section/assessment/utils";

import {
  type HomeAssessmentSection,
  type StudentHomeAssessmentSection,
  buildHomeAssessmentSections,
  buildStudentHomeAssessmentSections,
  getHomeAssessmentPublishLabel,
} from "./home-assessment-utils";

import { serverGetHomeAssessmentTable } from "api/api-server-assessment";
import {
  AssessmentPublishingStatus,
  HomeAssessmentTableRow,
  StudentHomeAssessmentTableRow,
} from "api/api-types-assessment";
import { LoadingScreen } from "components/LoadingScreen";
import {
  ASSESSMENT_VIEW_QUERY_PARAM,
  SECONDARY_OVERLAY_QUERY_PARAM,
  SecondaryOverlay,
  TERM_ID_QUERY_PARAM,
} from "components/constants";
import { getPageType } from "components/materials/page/helpers";
import { MarkAssessmentCompleteMenu } from "components/materials/section/assessment/helpers";
import { TermMenu } from "components/menus/TermMenu";
import { ActivityCategory, PageCategory } from "components/server-types";
import { ScrollableTableContainer } from "components/tables/ScrollableTableContainer";
import { TD, TH, Table } from "components/tables/TableElements";
import { t } from "i18n/i18n";
import { EmptyCard } from "mds/components/EmptyCard";
import { Tooltip } from "mds/components/Tooltip";
import { TooltipTrigger } from "mds/components/TooltipTrigger";
import { SquareButton } from "mds/components/buttons/SquareButton";
import {
  AssessPointsMarkerIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CircleInfoIcon,
  CommentIcon,
  EyeNotVisibleIcon,
} from "mds/icons";
import { useHomeRole } from "providers/HomeRoleProvider";
import { useHomeRoleCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  courseSelectors,
  selectDefaultTermIdForCurrentUser,
  selectSectionsForCurrentUser,
} from "store/selectors";
import { useListSelector, usePromiseEffect } from "store/store-hooks";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { Link, useNavigate, useQueryParam, useQueryParamsActions } from "utils/routing";
import { pageUrl, sectionHomeUrl } from "utils/urls";

type AssessorPageRowProps = {
  className?: string;
  sectionId: string;
  pageAssessmentRow: HomeAssessmentTableRow;
  refetch?: () => void;
};

const AssessorPageRow: FC<AssessorPageRowProps> = ({
  className,
  pageAssessmentRow,
  sectionId,
  refetch = () => {},
}) => {
  const isMarkedComplete = pageAssessmentRow.is_marked_complete;
  const showAssessed = pageAssessmentRow.is_assessment_complete || isMarkedComplete;

  return (
    <tr className={twMerge("border-b border-black-tint-90", className)}>
      <TD className="max-w-40 truncate px-4 py-2.5" title={pageAssessmentRow.topic_title || "-"}>
        {pageAssessmentRow.topic_title || "-"}
      </TD>

      <TD className="max-w-40 truncate py-2.5" title={pageAssessmentRow.title}>
        <div className="flex items-center gap-1">
          {pageAssessmentRow.hidden && (
            <TooltipTrigger
              content={t("assessment_dashboard.all.hidden_page_tooltip")}
              label={t("assessment_dashboard.all.hidden_page_tooltip")}
            >
              <EyeNotVisibleIcon className="shrink-0 text-black-tint-40" />
            </TooltipTrigger>
          )}
          <Link
            className="min-w-0 truncate text-blue-shade-20 underline"
            to={pageUrl(sectionId, pageAssessmentRow.page_id)}
            onClick={() => {
              trackEvent("Home - Assessments / View Page", {
                ...BUTTON_EVENT_CATEGORY,
                pageTitle: pageAssessmentRow.title,
                pageUrl: pageUrl(sectionId, pageAssessmentRow.page_id),
                pageId: pageAssessmentRow.page_id,
              });
            }}
          >
            {pageAssessmentRow.title}
          </Link>
        </div>
      </TD>

      <TD>
        <Tooltip
          content={
            pageAssessmentRow.unassessed_late_submissions_count > 0 &&
            t("assessment.late_assessment_count", {
              count: pageAssessmentRow.unassessed_late_submissions_count,
            })
          }
        >
          <SquareButton
            as={Link}
            eventName={
              showAssessed
                ? "Home - Assessments / Go to progress overview"
                : "Home - Assessments / Start assessing"
            }
            eventProps={{
              pageTitle: pageAssessmentRow.title,
              pageUrl: pageUrl(sectionId, pageAssessmentRow.page_id),
              pageId: pageAssessmentRow.page_id,
            }}
            kind={showAssessed ? "complete" : "accent"}
            size="xs"
            to={
              showAssessed
                ? pageUrl(sectionId, pageAssessmentRow.page_id, {
                    additionalParams: `${SECONDARY_OVERLAY_QUERY_PARAM}=${SecondaryOverlay.AssessmentSummary}`,
                  })
                : pageUrl(sectionId, pageAssessmentRow.page_id, {
                    additionalParams: `${ASSESSMENT_VIEW_QUERY_PARAM}=1`,
                  })
            }
          >
            {getAssessmentStatusLabel({
              isMarkedComplete,
              assessedSubmissionsCount: pageAssessmentRow.assessed_submissions_count,
              isAssessmentComplete: pageAssessmentRow.is_assessment_complete,
            })}

            {pageAssessmentRow.unassessed_late_submissions_count > 0 && <CircleInfoIcon />}
          </SquareButton>
        </Tooltip>
      </TD>

      <TD className={showAssessed ? "text-green-shade-40" : undefined}>
        {pageAssessmentRow.assessed_submissions_count}/{pageAssessmentRow.total_submissions_count}
      </TD>

      <TD
        className={twMerge(
          pageAssessmentRow.publishing === AssessmentPublishingStatus.published
            ? pageAssessmentRow.is_assessment_complete
              ? "text-green-shade-40"
              : "text-orange-shade-40"
            : null,
          isMarkedComplete && "text-green-shade-40",
        )}
      >
        {getHomeAssessmentPublishLabel(
          pageAssessmentRow.publishing,
          pageAssessmentRow.is_assessment_complete,
          isMarkedComplete,
        )}
      </TD>

      <TD>{formatAssessmentCategory(pageAssessmentRow.assessment_category)}</TD>

      <TD>{pageAssessmentRow.avg_points === "-" ? "–" : pageAssessmentRow.avg_points}</TD>

      <TD>{pageAssessmentRow.outcomes_count}</TD>

      <TD>
        {pageAssessmentRow.last_submitted_at
          ? t("common.date", {
              val: parseISO(pageAssessmentRow.last_submitted_at),
            })
          : null}
      </TD>

      <td>
        <MarkAssessmentCompleteMenu
          isMarkedComplete={isMarkedComplete}
          pageId={pageAssessmentRow.page_id}
          pageTitle={pageAssessmentRow.title}
          sectionId={sectionId}
          onChange={refetch}
        />
      </td>
    </tr>
  );
};

type StudentPageRowProps = {
  className?: string;
  sectionId: string;
  pageAssessmentRow: StudentHomeAssessmentTableRow;
};

const formatStudentPoints = (
  points: StudentHomeAssessmentTableRow["points"],
  maxPoints: StudentHomeAssessmentTableRow["max_points"],
): string => {
  if (points == null || maxPoints == null || Number(maxPoints) === 0) {
    return "–";
  }
  return `${points}/${maxPoints}`;
};

const StudentPageRow: FC<StudentPageRowProps> = ({ className, pageAssessmentRow, sectionId }) => (
  <tr className={twMerge("border-b border-black-tint-90", className)}>
    <TD className="max-w-40 truncate px-4 py-2.5" title={pageAssessmentRow.topic_title || "-"}>
      {pageAssessmentRow.topic_title || "-"}
    </TD>

    <TD className="py-2.5" title={pageAssessmentRow.title}>
      <div className="flex items-center gap-2">
        {pageAssessmentRow.hidden && (
          <TooltipTrigger
            content={t("assessment_dashboard.all.hidden_page_tooltip")}
            label={t("assessment_dashboard.all.hidden_page_tooltip")}
          >
            <EyeNotVisibleIcon className="shrink-0 text-black-tint-40" />
          </TooltipTrigger>
        )}
        <Link
          className="min-w-0 truncate text-blue-shade-20 underline"
          to={pageUrl(sectionId, pageAssessmentRow.page_id)}
          onClick={() => {
            trackEvent("Home - View Assessments", {
              ...BUTTON_EVENT_CATEGORY,
              pageTitle: pageAssessmentRow.title,
              pageUrl: pageUrl(sectionId, pageAssessmentRow.page_id),
              pageId: pageAssessmentRow.page_id,
            });
          }}
        >
          {pageAssessmentRow.title}
        </Link>
        {pageAssessmentRow.comment && (
          <TooltipTrigger
            content={pageAssessmentRow.comment}
            label={t("assessment.instructor_comment")}
            positionStrategy="fixed"
          >
            <CommentIcon className="text-blue-shade-40" size="l" />
          </TooltipTrigger>
        )}
      </div>
    </TD>
    <TD>
      {getPageType(
        pageAssessmentRow.category as PageCategory,
        pageAssessmentRow.activity_category as ActivityCategory | null,
      )}
    </TD>
    <TD>{formatStudentPoints(pageAssessmentRow.points, pageAssessmentRow.max_points)}</TD>
    <TD>{pageAssessmentRow.outcomes_count}</TD>
    <TD>
      {pageAssessmentRow.submitted_at
        ? t("common.date", {
            val: parseISO(pageAssessmentRow.submitted_at),
          })
        : null}
    </TD>
  </tr>
);

export const HomeAssessmentList: FC = () => {
  const homeRole = useHomeRole();
  const { canAuthorSection } = useHomeRoleCapabilities();
  const navigate = useNavigate();
  const { setQueryParams } = useQueryParamsActions();
  const termIdParam = useQueryParam(TERM_ID_QUERY_PARAM);
  const defaultTermId = useAppSelector(selectDefaultTermIdForCurrentUser);
  const selectedTermId = termIdParam || defaultTermId || "";
  const sectionsForRole = useListSelector(
    (state) => selectSectionsForCurrentUser(state, homeRole),
    ["id", "title", "course_id", "term_id"],
  );
  // TODO: create a selector for `sections` down below, so we avoid computing assessorSections and studentSections every time.
  // Doing so, we would not need coursesById here since it would be computed in the selector.
  // Courses should not change too often, so it is okay to disable the object warning.
  // eslint-disable-next-line @minervaproject/no-object-from-use-app-selector/no-object-from-use-app-selector
  const coursesById = useAppSelector(courseSelectors.selectEntities);

  const [dummyData, setDummyData] = useState({});
  const refetch = () => setDummyData({});

  const { data: assessmentRows, isLoading } = usePromiseEffect<
    HomeAssessmentTableRow[] | StudentHomeAssessmentTableRow[]
  >(() => {
    if (!selectedTermId || !homeRole) {
      return Promise.resolve([]);
    }
    return serverGetHomeAssessmentTable({
      term_id: selectedTermId,
      role: homeRole,
    });
  }, [selectedTermId, homeRole, dummyData]);

  const assessorSections = useMemo((): HomeAssessmentSection[] => {
    if (!canAuthorSection || assessmentRows === null) {
      return [];
    }
    const sectionsForTerm = sectionsForRole.filter((section) => section.term_id === selectedTermId);
    return buildHomeAssessmentSections(
      assessmentRows as HomeAssessmentTableRow[],
      sectionsForTerm,
      coursesById,
    );
  }, [assessmentRows, canAuthorSection, coursesById, sectionsForRole, selectedTermId]);

  const studentSections = useMemo((): StudentHomeAssessmentSection[] => {
    if (canAuthorSection || assessmentRows === null) {
      return [];
    }
    const sectionsForTerm = sectionsForRole.filter((section) => section.term_id === selectedTermId);
    return buildStudentHomeAssessmentSections(
      assessmentRows as StudentHomeAssessmentTableRow[],
      sectionsForTerm,
      coursesById,
    );
  }, [assessmentRows, canAuthorSection, coursesById, sectionsForRole, selectedTermId]);

  const sections = canAuthorSection ? assessorSections : studentSections;

  if (isLoading || assessmentRows === null) {
    return <LoadingScreen />;
  }

  return (
    <div className="flex h-full flex-col gap-8 overflow-y-auto text-xs">
      <TermMenu
        defaultToAll={false}
        selectedTermId={selectedTermId}
        showIcons={false}
        size="s"
        filterToCurrentUser
        hideIfNoTerms
        onTermChange={(termId) => setQueryParams({ [TERM_ID_QUERY_PARAM]: termId })}
      />

      {sections.length === 0 ? (
        <EmptyCard
          className="mb-4"
          description={t("assessment_dashboard.all.empty_description")}
          icon={<AssessPointsMarkerIcon className="text-blue" />}
          title={t("assessment_dashboard.all.empty_title")}
        />
      ) : (
        <ul className="flex flex-col gap-6">
          {sections.map((section) => (
            <li key={section.id}>
              <Disclosure key={section.id} defaultOpen>
                {({ open }) => {
                  return (
                    <>
                      <div className="flex w-full items-center justify-between border-b border-black-tint-90 py-2">
                        <SquareButton
                          kind="ghost"
                          size="s"
                          onClick={() => {
                            navigate(`${sectionHomeUrl(section.id)}?tab=assessments`);
                          }}
                        >
                          {section.code_title}: {section.title}
                        </SquareButton>

                        <DisclosureButton
                          aria-label={open ? t("tooltip.collapse") : t("tooltip.expand")}
                          className="flex flex-1 items-center justify-end self-stretch"
                          title={open ? t("tooltip.collapse") : t("tooltip.expand")}
                        >
                          {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
                        </DisclosureButton>
                      </div>

                      <DisclosurePanel className="overflow-x-hidden">
                        {section.pages.length === 0 ? (
                          <div className="pl-4 pt-2 text-xs text-black-tint-40">
                            {t("home.assessments.no_assessments_for_section")}
                          </div>
                        ) : (
                          <ScrollableTableContainer className="mb-0">
                            <Table>
                              <thead className="border-b border-black-tint-70">
                                <tr>
                                  <TH className="pl-4">{t("glossary.topic")}</TH>

                                  <TH>{t("glossary.page")}</TH>

                                  {canAuthorSection && (
                                    <>
                                      <TH>{t("glossary.status")}</TH>
                                      <TH>{t("glossary.progress")}</TH>
                                      <TH>{t("publishing.title")}</TH>
                                    </>
                                  )}

                                  <TH>{t("assessment_categories.title")}</TH>

                                  <TH>
                                    {canAuthorSection
                                      ? t("glossary.avg_points")
                                      : t("glossary.points")}
                                  </TH>

                                  <TH>{t("glossary.outcomes")}</TH>

                                  <TH>
                                    {canAuthorSection
                                      ? t("glossary.last_submitted")
                                      : t("glossary.submitted")}
                                  </TH>

                                  {canAuthorSection && (
                                    <th className="sr-only">{t("glossary.actions")}</th>
                                  )}
                                </tr>
                              </thead>

                              <tbody>
                                {canAuthorSection
                                  ? (section as HomeAssessmentSection).pages.map(
                                      (pageAssessmentRow) => (
                                        <AssessorPageRow
                                          key={pageAssessmentRow.page_id}
                                          pageAssessmentRow={pageAssessmentRow}
                                          refetch={refetch}
                                          sectionId={section.id}
                                        />
                                      ),
                                    )
                                  : (section as StudentHomeAssessmentSection).pages.map(
                                      (pageAssessmentRow) => (
                                        <StudentPageRow
                                          key={pageAssessmentRow.page_id}
                                          pageAssessmentRow={pageAssessmentRow}
                                          sectionId={section.id}
                                        />
                                      ),
                                    )}
                              </tbody>
                            </Table>
                          </ScrollableTableContainer>
                        )}
                      </DisclosurePanel>
                    </>
                  );
                }}
              </Disclosure>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
